import { useCallback } from "react";
import { trpc } from "lib/trpc";
import { ClientDocument } from "@compass/shared/types";

export interface UploadDocumentHandlerOptions {
    filename: string;
    description?: string;
    file: File;
    aiOptOut?: boolean;
    context?: "PUBLIC_ATTACHMENT" | "PRIVATE_ATTACHMENT";
    // Optional hint for the admin upload notification (PRTL-1322) — where the
    // upload happened, so the alert reads "…in a discussion" / "…to their
    // documents". Omit for generic wording.
    notifyContext?: "discussion" | "conversation" | "social" | "documents";
}

export type UploadDocumentHandler = (args: UploadDocumentHandlerOptions) => Promise<ClientDocument>;

export const useUploadDocument = (): UploadDocumentHandler => {
    const queryClient = trpc.useContext();
    const getUploadUrl = trpc.userContent.upload.start.useMutation();
    const completeUpload = trpc.userContent.upload.complete.useMutation();

    return useCallback(
        async ({
            filename,
            description,
            file,
            aiOptOut,
            context,
            notifyContext,
        }: UploadDocumentHandlerOptions) => {
            const contentType = file.type;

            const {
                file: { id: fileId },
                uploadUrl,
            } = await getUploadUrl.mutateAsync({
                filename: filename || undefined,
                originalFilename: file.name,
                description,
                contentType,
                aiOptOut,
                context,
            });

            const bufferFile = await file.arrayBuffer();

            const result = await fetch(uploadUrl, {
                method: "PUT",
                body: bufferFile,
            });

            if (result.status !== 200) {
                throw new Error("Failed to upload file");
            }

            return await completeUpload.mutateAsync(
                { fileId, notifyContext },
                {
                    onSuccess: () => {
                        queryClient.userContent.latest.invalidate();
                    },
                    onSettled: () => {
                        queryClient.userContent.list.invalidate();
                    },
                },
            );
        },
        [queryClient, getUploadUrl, completeUpload],
    );
};
